import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { MapPin, Calendar, Phone, GripVertical, Users, Search, Filter, X } from 'lucide-react';
import { Input } from '@/components/ui/input';

import {
  DndContext,
  DragOverlay,
  useSensor,
  useSensors,
  PointerSensor,
  closestCorners,
  useDroppable,
  useDraggable,
} from '@dnd-kit/core';

const STAGES = [
  { key: 'new', label: 'New', color: '#3b82f6', ring: 'ring-blue-400/40', bg: 'bg-blue-500/8', headerBg: 'bg-blue-500' },
  { key: 'quotation_sent', label: 'Quotation Sent', color: '#8b5cf6', ring: 'ring-violet-400/40', bg: 'bg-violet-500/8', headerBg: 'bg-violet-500' },
  { key: 'followup', label: 'Follow-up', color: '#f59e0b', ring: 'ring-amber-400/40', bg: 'bg-amber-500/8', headerBg: 'bg-amber-500' },
  { key: 'negotiation', label: 'Negotiation', color: '#f97316', ring: 'ring-orange-400/40', bg: 'bg-orange-500/8', headerBg: 'bg-orange-500' },
  { key: 'confirmed', label: 'Confirmed', color: '#10b981', ring: 'ring-emerald-400/40', bg: 'bg-emerald-500/8', headerBg: 'bg-emerald-500' },
  { key: 'lost', label: 'Lost', color: '#ef4444', ring: 'ring-red-400/40', bg: 'bg-red-500/8', headerBg: 'bg-red-500' },
];

const PRIORITY_CONFIG = {
  low: { color: 'bg-slate-400', label: 'Low' },
  medium: { color: 'bg-blue-400', label: 'Medium' },
  high: { color: 'bg-orange-400', label: 'High' },
  urgent: { color: 'bg-red-500', label: 'Urgent' },
};

/* ── Draggable Lead Card ──────────────────────────────────── */
function DraggableCard({ lead, onClick }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: lead.id,
    data: { lead },
  });

  return (
    <div
      ref={setNodeRef}
      className={`transition-all duration-200 ${isDragging ? 'opacity-30 scale-95' : 'opacity-100'}`}
      data-testid={`pipeline-card-${lead.id}`}
    >
      <Card className="border border-border/60 bg-card hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 group">
        <CardContent className="p-3">
          {/* Drag handle + name row */}
          <div className="flex items-start gap-2">
            <button
              {...listeners}
              {...attributes}
              className="mt-0.5 p-0.5 rounded cursor-grab active:cursor-grabbing text-muted-foreground/40 hover:text-muted-foreground/70 transition-colors shrink-0"
              data-testid={`drag-handle-${lead.id}`}
              aria-label="Drag to move"
            >
              <GripVertical className="w-3.5 h-3.5" />
            </button>
            <div className="flex-1 min-w-0 cursor-pointer" onClick={onClick}>
              <div className="flex items-center justify-between gap-1">
                <span className="font-heading font-semibold text-sm text-foreground truncate">
                  {lead.client_name || 'Unknown'}
                </span>
                <div
                  className={`w-2 h-2 rounded-full shrink-0 ${PRIORITY_CONFIG[lead.priority]?.color || 'bg-slate-400'}`}
                  title={lead.priority}
                />
              </div>
              <p className="text-[10px] text-muted-foreground font-mono mt-0.5">{lead.inquiry_id}</p>
            </div>
          </div>

          {/* Details */}
          <div className="space-y-1 text-xs text-muted-foreground mt-2 ml-6">
            {lead.destination && (
              <div className="flex items-center gap-1.5">
                <MapPin className="w-3 h-3 shrink-0 text-primary/60" />
                <span className="truncate">{lead.destination}</span>
              </div>
            )}
            {lead.travel_date && (
              <div className="flex items-center gap-1.5">
                <Calendar className="w-3 h-3 shrink-0 text-primary/60" />
                <span>{lead.travel_date}</span>
              </div>
            )}
            {lead.budget && (
              <div className="flex items-center gap-1.5 text-secondary font-medium">
                <span>{lead.budget}</span>
              </div>
            )}
          </div>

          {/* Tags */}
          <div className="flex items-center gap-1 mt-2 ml-6">
            {lead.booking_type && (
              <Badge variant="outline" className="text-[10px] capitalize px-1.5 py-0">{lead.booking_type}</Badge>
            )}
            {lead.is_closed && (
              <Badge className="bg-slate-500/80 text-white text-[10px] px-1.5 py-0">Closed</Badge>
            )}
            {lead.assigned_to_name && (
              <div className="ml-auto flex items-center gap-1 text-[10px] text-muted-foreground">
                <Users className="w-2.5 h-2.5" />
                <span className="truncate max-w-[60px]">{lead.assigned_to_name}</span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/* ── Drag Overlay (ghost card while dragging) ─────────────── */
function DragOverlayCard({ lead }) {
  const stage = STAGES.find(s => s.key === lead.stage);
  return (
    <div className="w-72 rotate-2 shadow-2xl" data-testid="drag-overlay-card">
      <Card className="border-2 bg-card" style={{ borderColor: stage?.color }}>
        <CardContent className="p-3">
          <div className="flex items-start gap-2">
            <GripVertical className="w-3.5 h-3.5 text-muted-foreground/50 mt-0.5" />
            <div className="flex-1 min-w-0">
              <span className="font-heading font-semibold text-sm text-foreground truncate block">
                {lead.client_name || 'Unknown'}
              </span>
              <p className="text-[10px] text-muted-foreground font-mono mt-0.5">{lead.inquiry_id}</p>
            </div>
          </div>
          <div className="space-y-1 text-xs text-muted-foreground mt-2 ml-6">
            {lead.destination && (
              <div className="flex items-center gap-1.5">
                <MapPin className="w-3 h-3 text-primary/60" />
                <span className="truncate">{lead.destination}</span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/* ── Droppable Column ─────────────────────────────────────── */
function StageColumn({ stage, leads, onCardClick, isOver }) {
  const { setNodeRef } = useDroppable({ id: stage.key });
  const count = leads.length;

  return (
    <div
      ref={setNodeRef}
      className={`flex-shrink-0 w-72 flex flex-col rounded-xl transition-all duration-300 ${
        isOver ? `ring-2 ${stage.ring} scale-[1.01]` : ''
      }`}
      data-testid={`pipeline-col-${stage.key}`}
    >
      {/* Column Header */}
      <div className="flex items-center gap-2.5 mb-2 px-1">
        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: stage.color }} />
        <span className="font-heading font-semibold text-sm text-foreground">{stage.label}</span>
        <Badge
          variant="secondary"
          className="text-[10px] ml-auto tabular-nums h-5 min-w-[20px] flex items-center justify-center"
        >
          {count}
        </Badge>
      </div>

      {/* Cards Container */}
      <div
        className={`rounded-xl border border-border/50 p-2 flex-1 overflow-y-auto transition-colors duration-300 ${
          isOver ? 'bg-primary/5 border-primary/20' : stage.bg
        }`}
        style={{ maxHeight: 'calc(100vh - 240px)', minHeight: '200px' }}
      >
        <div className="space-y-2">
          {leads.map(lead => (
            <DraggableCard
              key={lead.id}
              lead={lead}
              onClick={() => onCardClick(lead.id)}
            />
          ))}
          {count === 0 && (
            <div className={`text-center py-12 text-xs transition-all duration-300 ${
              isOver ? 'text-primary font-medium' : 'text-muted-foreground'
            }`}>
              {isOver ? 'Drop here' : 'No leads'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Main Kanban Board ────────────────────────────────────── */
export default function PipelineBoard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterPriority, setFilterPriority] = useState('all');

  // Drag state
  const [activeId, setActiveId] = useState(null);
  const [overId, setOverId] = useState(null);

  // Closure dialog
  const [showClosureDialog, setShowClosureDialog] = useState(false);
  const [closureLead, setClosureLead] = useState(null);
  const [closureStage, setClosureStage] = useState('');
  const [closureReason, setClosureReason] = useState('');
  const [closureLoading, setClosureLoading] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  useEffect(() => { fetchLeads(); }, []);

  const fetchLeads = async () => {
    try {
      const res = await api.get('/leads');
      setLeads(res.data);
    } catch {
      toast.error('Failed to load pipeline');
    } finally {
      setLoading(false);
    }
  };

  // Filter + search
  const filteredLeads = useMemo(() => {
    let result = leads;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(l =>
        (l.client_name || '').toLowerCase().includes(q) ||
        (l.destination || '').toLowerCase().includes(q) ||
        (l.inquiry_id || '').toLowerCase().includes(q)
      );
    }
    if (filterPriority !== 'all') {
      result = result.filter(l => l.priority === filterPriority);
    }
    return result;
  }, [leads, searchQuery, filterPriority]);

  const groupedLeads = useMemo(() => {
    const groups = {};
    STAGES.forEach(s => { groups[s.key] = filteredLeads.filter(l => l.stage === s.key); });
    return groups;
  }, [filteredLeads]);

  const activeLead = useMemo(() =>
    activeId ? leads.find(l => l.id === activeId) : null
  , [activeId, leads]);

  // ── Stage move logic ──
  const moveStage = useCallback(async (leadId, newStage) => {
    // Closure stages need dialog
    if (newStage === 'confirmed' || newStage === 'lost') {
      const lead = leads.find(l => l.id === leadId);
      setClosureLead(lead);
      setClosureStage(newStage);
      setClosureReason('');
      setShowClosureDialog(true);
      return;
    }
    // Optimistic update
    setLeads(prev => prev.map(l => l.id === leadId ? { ...l, stage: newStage } : l));
    try {
      await api.put(`/leads/${leadId}/stage?stage=${newStage}`);
      toast.success('Stage updated');
    } catch {
      toast.error('Failed to update stage');
      fetchLeads(); // Revert on failure
    }
  }, [leads]);

  const confirmClosure = async () => {
    if (!closureReason.trim()) {
      toast.error('Please provide a reason/remark');
      return;
    }
    setClosureLoading(true);
    try {
      await api.put(
        `/leads/${closureLead.id}/stage?stage=${closureStage}&closure_reason=${encodeURIComponent(closureReason)}`
      );
      setLeads(prev => prev.map(l =>
        l.id === closureLead.id
          ? { ...l, stage: closureStage, is_closed: true, closure_reason: closureReason }
          : l
      ));
      toast.success(`Lead marked as ${closureStage}`);
      setShowClosureDialog(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setClosureLoading(false);
    }
  };

  // ── DnD Handlers ──
  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragOver = (event) => {
    setOverId(event.over?.id || null);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);
    setOverId(null);

    if (!over) return;

    const leadId = active.id;
    const lead = leads.find(l => l.id === leadId);
    if (!lead) return;

    // Determine target stage — could be dropping on a column or on another card
    let targetStage = over.id;
    // If dropped on a card, get that card's stage
    if (!STAGES.find(s => s.key === targetStage)) {
      const targetLead = leads.find(l => l.id === targetStage);
      if (targetLead) {
        targetStage = targetLead.stage;
      } else {
        return;
      }
    }

    if (lead.stage === targetStage) return;
    if (lead.is_closed) {
      toast.error('Cannot move a closed lead');
      return;
    }

    moveStage(leadId, targetStage);
  };

  const handleDragCancel = () => {
    setActiveId(null);
    setOverId(null);
  };

  // ── Stats summary ──
  const totalLeads = leads.length;
  const activeLeads = leads.filter(l => !l.is_closed).length;

  if (loading) {
    return (
      <div className="space-y-4 animate-fade-in" data-testid="pipeline-loading">
        <div className="h-8 w-48 bg-muted rounded animate-pulse" />
        <div className="flex gap-4 overflow-x-auto">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <div key={i} className="w-72 h-96 bg-muted rounded-xl animate-pulse flex-shrink-0" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 animate-fade-in" data-testid="pipeline-board">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground tracking-tight">
            Pipeline
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Drag cards between stages to update &middot; {activeLeads} active of {totalLeads} total
          </p>
        </div>

        {/* Search + Filter bar */}
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <Input
              placeholder="Search leads..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8 h-8 w-48 text-xs"
              data-testid="pipeline-search"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
          <div className="flex items-center gap-1">
            <Filter className="w-3.5 h-3.5 text-muted-foreground" />
            {['all', 'urgent', 'high', 'medium', 'low'].map(p => (
              <button
                key={p}
                onClick={() => setFilterPriority(p)}
                className={`px-2 py-0.5 rounded-full text-[10px] font-medium transition-all ${
                  filterPriority === p
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
                data-testid={`pipeline-filter-${p}`}
              >
                {p === 'all' ? 'All' : p.charAt(0).toUpperCase() + p.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Closure Dialog */}
      <Dialog open={showClosureDialog} onOpenChange={setShowClosureDialog}>
        <DialogContent data-testid="pipeline-closure-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {closureStage === 'confirmed' ? 'Confirm Booking' : 'Mark as Lost'}
            </DialogTitle>
            <DialogDescription>
              {closureLead?.client_name && (
                <span className="font-medium text-foreground">{closureLead.client_name}</span>
              )}
              {' '}&mdash; A mandatory reason/remark is required before{' '}
              {closureStage === 'confirmed' ? 'confirming' : 'closing'} this lead.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Textarea
              placeholder={
                closureStage === 'confirmed'
                  ? 'e.g., Payment received, booking confirmed...'
                  : 'e.g., Client chose competitor, budget issues...'
              }
              value={closureReason}
              onChange={(e) => setClosureReason(e.target.value)}
              rows={3}
              data-testid="pipeline-closure-reason"
            />
            <div className="flex gap-2">
              <Button
                onClick={confirmClosure}
                disabled={closureLoading}
                className={`flex-1 text-white ${
                  closureStage === 'confirmed'
                    ? 'bg-emerald-600 hover:bg-emerald-700'
                    : 'bg-red-600 hover:bg-red-700'
                }`}
                data-testid="pipeline-confirm-closure"
              >
                {closureLoading ? 'Updating...' : closureStage === 'confirmed' ? 'Confirm Booking' : 'Mark Lost'}
              </Button>
              <Button variant="outline" onClick={() => setShowClosureDialog(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Kanban Board */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <div className="flex gap-4 overflow-x-auto pb-4" data-testid="pipeline-columns">
          {STAGES.map(stage => (
            <StageColumn
              key={stage.key}
              stage={stage}
              leads={groupedLeads[stage.key]}
              onCardClick={(id) => navigate(`/dashboard/leads/${id}`)}
              isOver={overId === stage.key}
            />
          ))}
        </div>

        <DragOverlay dropAnimation={{ duration: 200, easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)' }}>
          {activeLead ? <DragOverlayCard lead={activeLead} /> : null}
        </DragOverlay>
      </DndContext>
    </div>
  );
}
